import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Building, Pencil, Trash2 } from 'lucide-react';
import { useActiveTabInView } from '../hooks/useActiveTabInView';
import { useClient, useDeleteClient, useUpdateClient } from '../hooks/useClients';
import type { ClientInput } from '../lib/clientsApi';
import { FadeIn, SkeletonCard } from '../components/ui/Skeleton';
import { ClientFormModal } from '../components/clients/ClientFormModal';
import { ClientOverviewTab } from '../components/clients/ClientOverviewTab';
import { ClientInvoicesTab } from '../components/clients/ClientInvoicesTab';
import { ClientProjectsTab } from '../components/clients/ClientProjectsTab';
import { ClientResourcesTab } from '../components/clients/ClientResourcesTab';
import { ClientTeamTab } from '../components/clients/ClientTeamTab';
import { ClientCalendarTab } from '../components/clients/ClientCalendarTab';
import { ClientActorsTab } from '../components/clients/ClientActorsTab';
import { ClientWhatsAppTab } from '../components/clients/ClientWhatsAppTab';
import { usePermissions } from '../hooks/usePermissions';
import { useModules } from '../hooks/useModules';
import { TasksView } from '../components/tasks/TasksView';
import { clientColor } from '../lib/clientLabel';
import { MeetingNotesTab } from '../components/meetingNotes/MeetingNotesTab';
import { useConfirm } from '../components/ui/ConfirmProvider';

const TABS = [
  'overview',
  'calendar',
  'tasks',
  'team',
  'invoices',
  'projects',
  'resources',
  'actors',
  'whatsapp',
  'notes',
] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Prezentare generală',
  calendar: 'Calendar',
  tasks: 'Sarcini',
  team: 'Echipă',
  invoices: 'Facturi',
  projects: 'Proiecte',
  resources: 'Resurse',
  actors: 'Actori',
  whatsapp: 'WhatsApp',
  notes: 'Notițe ședință',
};

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: client, isLoading, isError } = useClient(id);
  const updateMutation = useUpdateClient(id ?? '');
  const deleteMutation = useDeleteClient();
  const confirm = useConfirm();
  const { can } = usePermissions();
  const { isOn } = useModules();
  // Tabs of product modules that can be off for this instance (Firmio Console
  // / Settings → Module); Calendar also needs the posts permission.
  const tabVisible: Partial<Record<Tab, boolean>> = {
    calendar: can('socialPosts', 'canView'),
    tasks: isOn('clientTasks'),
    team: isOn('clientTeam'),
    invoices: isOn('invoices'),
    // Actori: opt-in module (Genius Media) + actors.canView.
    actors: isOn('actors') && can('actors', 'canView'),
    // WhatsApp: opt-in module + its own permission (the conversations).
    whatsapp: isOn('whatsapp') && can('whatsapp', 'canView'),
  };
  const tabs = TABS.filter((t) => tabVisible[t] !== false);

  const initialTab = (window.location.hash.replace('#', '') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab) ? initialTab : 'overview');
  const [showEdit, setShowEdit] = useState(false);
  const tabsRef = useActiveTabInView<HTMLElement>(tab, client);

  function selectTab(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  async function handleEdit(input: ClientInput) {
    await updateMutation.mutateAsync(input);
    setShowEdit(false);
  }

  async function handleDelete() {
    if (!id) return;
    if (!(await confirm({ message: 'Marchezi acest client ca inactiv?', danger: true }))) return;
    await deleteMutation.mutateAsync(id);
  }

  if (isLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <SkeletonCard lines={4} />
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <Link to="/clients" className="text-sm text-brand-700 hover:underline">
          ← Înapoi la clienți
        </Link>
        <p className="mt-4 text-red-600">Clientul nu a fost găsit.</p>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft size={14} /> Clienți
      </Link>

      {/* Header */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Building size={24} />
          </div>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: clientColor(client) }}
                aria-hidden
              />
              {client.shortName || client.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
              {client.shortName && client.shortName !== client.name && <span>{client.name}</span>}
              {client.cifCui && <span>CIF {client.cifCui}</span>}
              {client.country && <span>· {client.country}</span>}
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                  client.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {client.isActive ? 'Activ' : 'Inactiv'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Pencil size={15} /> Editează
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 size={15} /> Dezactivează
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav ref={tabsRef} className="relative -mb-px flex gap-6 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => selectTab(t)}
              aria-current={tab === t ? 'page' : undefined}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                tab === t
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {TAB_LABELS[t]}
              {t === 'projects' && client.counts.projects > 0 && (
                <span className="ml-1 text-xs text-gray-400">({client.counts.projects})</span>
              )}
              {t === 'team' && client.members.length > 0 && (
                <span className="ml-1 text-xs text-gray-400">({client.members.length})</span>
              )}
              {t === 'resources' && client.counts.resources > 0 && (
                <span className="ml-1 text-xs text-gray-400">({client.counts.resources})</span>
              )}
              {t === 'notes' && client.counts.meetingNotes > 0 && (
                <span className="ml-1 text-xs text-gray-400">({client.counts.meetingNotes})</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        <FadeIn>
          {tab === 'overview' && <ClientOverviewTab client={client} />}
          {tab === 'calendar' && tabVisible.calendar && <ClientCalendarTab clientId={client.id} />}
          {tab === 'tasks' && tabVisible.tasks && (
            <TasksView
              clientId={client.id}
              createProjectId={client.defaultProjectId ?? undefined}
              seedFromUrl={false}
            />
          )}
          {tab === 'team' && tabVisible.team && (
            <ClientTeamTab clientId={client.id} members={client.members} />
          )}
          {tab === 'invoices' && tabVisible.invoices && <ClientInvoicesTab clientId={client.id} />}
          {tab === 'projects' && <ClientProjectsTab projects={client.projects} />}
          {tab === 'resources' && (
            <ClientResourcesTab
              clientId={client.id}
              clientName={client.name}
              resources={client.resources}
            />
          )}
          {tab === 'actors' && tabVisible.actors && (
            <ClientActorsTab client={{ id: client.id, name: client.name }} />
          )}
          {tab === 'whatsapp' && tabVisible.whatsapp && (
            <ClientWhatsAppTab client={{ id: client.id, name: client.name }} />
          )}
          {tab === 'notes' && <MeetingNotesTab owner={{ clientId: client.id }} />}
        </FadeIn>
      </div>

      {showEdit && (
        <ClientFormModal
          title="Editează clientul"
          initial={client}
          onClose={() => setShowEdit(false)}
          onSubmit={handleEdit}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  );
}
